<template>
  <v-card color="sexta">
    <v-card-title>Destinos e ordem das fases</v-card-title>
    <v-card-text>
      <p>Marque os destinos permitidos para os cards da fase <strong>{{ fase.nome }}</strong>.</p>
      <p class="mb-2">Arraste as linhas ou use as setas para ordenar as colunas de <strong>todo o quadro</strong>. As alterações serão aplicadas ao salvar.</p>
      <v-alert v-if="conflito" type="warning" dense outlined>
        O quadro mudou em outra sessão. Cancele e reabra esta tela para usar a lista atualizada.
      </v-alert>
      <div class="c-lista-destinos">
        <Container group-name="ordemFasesModal" drag-handle-selector=".c-alca-destino--ativa"
          :should-accept-drop="aceitarArraste" :get-child-payload="payloadFase"
          :auto-scroll-enabled="true" :drag-begin-delay="100"
          :drop-placeholder="{ className: 'c-placeholder-destino' }"
          @drag-start="iniciarArraste" @drag-end="terminarArraste" @drop="soltarFase">
          <Draggable v-for="(destino, index) in listaFases" :key="destino.id">
            <div class="c-linha-destino" :data-destino-id="destino.id">
              <span v-if="index === 0" class="c-fase-fixa" title="A fase inicial permanece na primeira posição.">
                <v-icon small aria-label="Fase inicial fixa">lock_outline</v-icon>
              </span>
              <v-btn v-else icon small class="c-alca-destino" :class="{ 'c-alca-destino--ativa': podeEditar }"
                :disabled="!podeEditar" :aria-label="'Arrastar fase ' + destino.nome" title="Arraste para reordenar">
                <v-icon>drag_indicator</v-icon>
              </v-btn>
              <v-checkbox v-model="destinos" :value="destino.id" :label="destino.nome"
                :disabled="destino.id === fase.id || boardEditing.locked || conflito" hide-details class="ma-0 pa-0 c-destino-checkbox" />
              <v-btn icon small :disabled="!podeEditar || boardEditing.locked || index < 2" :aria-label="'Subir fase ' + destino.nome"
                title="Subir fase" @click="moverFase(index, index - 1)"><v-icon small>arrow_upward</v-icon></v-btn>
              <v-btn icon small :disabled="!podeEditar || boardEditing.locked || index === 0 || index === listaFases.length - 1" :aria-label="'Descer fase ' + destino.nome"
                title="Descer fase" @click="moverFase(index, index + 1)"><v-icon small>arrow_downward</v-icon></v-btn>
            </div>
          </Draggable>
        </Container>
      </div>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn text :disabled="boardEditing.locked" @click="fechar">Cancelar</v-btn>
      <v-btn color="primeira" class="sexta--text" :disabled="!podeEditar || boardEditing.locked"
        :loading="boardEditing.state.busy" @click="salvar">Salvar</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import axios from "axios";
import { Container, Draggable } from "vue-smooth-dnd";
import { urlApi, showError } from "@/global";
export default {
  name: "opMoverCards",
  inject: ["boardEditing"],
  components: { Container, Draggable },
  props: { fase: Object, fases: Array },
  data() {
    const ids = this.fase.opcoes.fasesPermitidas
      .filter(Number.isInteger)
      .map(position => this.fases[position])
      .filter(Boolean)
      .map(fase => fase.id);
    return {
      destinos: [...new Set([this.fase.id, ...ids])],
      listaFases: this.fases.map(({ id, nome }) => ({ id, nome })),
      ordemAnterior: this.fases.map(fase => fase.id),
      conflito: false
    };
  },
  computed: {
    podeEditar() { return this.boardEditing.state.active && !this.boardEditing.state.busy && !this.conflito; }
  },
  methods: {
    payloadFase(index) { return { id: this.listaFases[index].id }; },
    aceitarArraste(source, payload) { return this.podeEditar && source.groupName === 'ordemFasesModal' && payload.id !== 1; },
    iniciarArraste({ isSource }) { if (isSource && this.podeEditar) this.boardEditing.beginDrag(); },
    terminarArraste() { this.boardEditing.endDrag(); },
    soltarFase({ removedIndex, addedIndex }) {
      this.boardEditing.endDrag();
      if (removedIndex === null || addedIndex === null) return;
      this.moverFase(removedIndex, Math.max(1, addedIndex));
    },
    moverFase(from, to) {
      if (!this.podeEditar || this.boardEditing.locked || from < 1 || from >= this.listaFases.length || to < 1 || to >= this.listaFases.length || from === to) return;
      this.listaFases.splice(to, 0, this.listaFases.splice(from, 1)[0]);
    },
    fechar() {
      if (!this.boardEditing.locked) this.$emit("fechar");
    },
    async salvar() {
      if (!this.podeEditar || this.boardEditing.locked) return;
      const body = {
        ordemAnterior: [...this.ordemAnterior], ordem: this.listaFases.map(fase => fase.id),
        destinos: { idFase: this.fase.id, fasesPermitidasIds: [...this.destinos] }
      };
      try {
        const { data } = await this.boardEditing.run(config => axios.patch(urlApi + '/fase/ordem', body, config));
        this.$emit("salvo", data);
        this.$emit("fechar");
        this.$toasted.global.defaultSuccess({ msg: "Destinos e ordem das fases salvos." });
      } catch (error) {
        if (error.response && error.response.status === 409) {
          this.conflito = true;
          this.$emit('conflito');
        }
        showError(error);
      }
    }
  },
  beforeDestroy() { this.boardEditing.endDrag(); }
};
</script>

<style scoped>
.c-lista-destinos { max-height: 55vh; overflow-y: auto; padding: 4px; }
.c-linha-destino { display: flex; align-items: center; gap: 4px; min-height: 42px; background: white; }
.c-destino-checkbox { flex: 1; min-width: 0; }
.c-destino-checkbox ::v-deep label { font-size: 14px; }
.c-alca-destino--ativa { cursor: grab; touch-action: none; }
.c-placeholder-destino { min-height: 42px; border: 1px dashed #363494; background: #e7edfb; }
</style>
